import type {
  CalculationSettingsRequestBody,
  CalculationSettingsTemplateDownloadRequest,
} from '@/api';
import { getApiClient } from '@/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const CALCULATION_SETTINGS_QUERY_KEY = 'calculationSettings';

export const useCalculationTypes = () =>
  useQuery({
    queryKey: ['calculationTypes'],
    queryFn: () => getApiClient().getCalculationTypes(),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const useCalculationSettings = () =>
  useQuery({
    queryKey: [CALCULATION_SETTINGS_QUERY_KEY],
    queryFn: () => getApiClient().calculationSettings(),
  });

export const useGetCalculationSettingsByPriceUnitIdAndYear = (
  priceUnitId: number,
  year: number,
) =>
  useQuery({
    queryKey: ['calculationSettings', priceUnitId, year],
    queryFn: () =>
      getApiClient().getCalculationSettingsForPriceUnitIdAndYear({
        priceUnitId,
        year,
      }),
    enabled:
      Number.isFinite(priceUnitId) && priceUnitId > 0 && Number.isFinite(year),
  });

export const useCreateCalculationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CalculationSettingsRequestBody) =>
      getApiClient().createCalculationSettings({
        calculationSettingsRequestBody: request,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CALCULATION_SETTINGS_QUERY_KEY],
      });
    },
  });
};

export const useUpdateCalculationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: {
      id: number;
      calculationSettingsRequestBody: CalculationSettingsRequestBody;
    }) =>
      getApiClient().updateCalculationSettings({
        id: request.id,
        calculationSettingsRequestBody: request.calculationSettingsRequestBody,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CALCULATION_SETTINGS_QUERY_KEY],
      });
    },
  });
};

export const useTemplateDownload = () =>
  useMutation({
    mutationFn: (request: CalculationSettingsTemplateDownloadRequest) =>
      getApiClient().downloadTemplate({
        calculationSettingsTemplateDownloadRequest: request,
      }),
  });

export const useGetUploadTypes = () =>
  useQuery({
    queryKey: ['uploadTypes'],
    queryFn: () => getApiClient().getCalculationSettingsUploadTargets(),
  });

export const useGetUploadReport = () =>
  useMutation({
    mutationFn: (fileId: string) =>
      getApiClient()
        .processCalculationSettingsUpload({ logId: fileId })
        .then((response) => response),
  });

export const useGetCalculationSettingsPresignedPutUrl = (target: string) =>
  useMutation({
    mutationFn: (fileName: string) =>
      getApiClient().getCalculationSettingsPresignedPutUrl({
        createPresignedUrlRequest: { fileName, target },
      }),
  });
